<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  modelValue?: number | string,
  id: string,
  placeholder: string,
  autofocus?: boolean,
  hasError?: boolean,
  hasInfo?: boolean,
  required?: boolean,
  type?: 'text' | 'number' | 'email' | 'tel',
  autocomplete?: string,
}>(), { type: 'text' })

const emit = defineEmits(['update:modelValue', 'input'])

const isNumeric = computed(() => props.type === 'number')

const sanitizeInput = (event: Event) => {
  if (!isNumeric.value) {
    return
  }
  const target = event.target as HTMLInputElement
  target.value = target.value.replace(/[^0-9.]/g, '')
}

const getValue = (event: Event): string | number => {
  const target = event.target as HTMLInputElement;
  if (!target) {
    return '';
  }

  if (!isNumeric.value) {
    return target.value;
  }

  const numericValue = parseFloat(target.value);
  return isNaN(numericValue) ? 0 : numericValue;
};

const emitInput = (event: Event) => {
  sanitizeInput(event)
  const value = getValue(event)
  emit('update:modelValue', value)
  emit('input', value)
}

const errorId = computed(() => `${props.id}-error`)
const infoId = computed(() => `${props.id}-info`)
const describedBy = computed(() => {
  if (props.hasError) return errorId.value
  if (props.hasInfo) return infoId.value
  return undefined
})
</script>

<template>
  <div class="flex flex-col gap-2">
    <label v-if="$slots.label" :for="id" class="flex w-full pl-2 text-sm font-bold text-slate-600" :class="{
      'text-red-600': hasError,
      'text-slate-500': !hasError && hasInfo,
    }">
      <slot name="label" />
      <!-- WCAG 3.3.2 — testo sr-only per obbligatorietà, non solo asterisco visivo -->
      <span v-if="required" class="sr-only"> (obbligatorio)</span>
    </label>
    <div>
      <div class="flex h-10 w-full">
        <input :id="id" :value="modelValue" :type="type" :min="isNumeric ? 1 : undefined" :name="id"
          class="block flex-1 rounded-[30px] border-0 bg-slate-200 px-4 text-gray-900 placeholder:text-gray-500 sm:text-sm focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-2"
          :class="{ 'ring-2 ring-red-400': hasError }"
          :placeholder="placeholder" :autofocus="autofocus"
          :autocomplete="autocomplete"
          :aria-required="required ? 'true' : undefined"
          :aria-invalid="hasError ? 'true' : 'false'"
          :aria-describedby="describedBy"
          @input="emitInput" />
      </div>
    </div>
    <!-- WCAG 3.3.1 — messaggio errore con role=alert e ID collegato tramite aria-describedby -->
    <span v-if="hasError" :id="errorId" role="alert" class="flex w-full pl-2 text-xs font-bold text-red-600">
      <slot name="error" />
    </span>
    <span v-if="!hasError && hasInfo" :id="infoId" class="flex w-full pl-2 text-xs font-bold text-slate-500">
      <slot name="info" />
    </span>
  </div>
</template>
